import {useEffect, useMemo, useState} from "react";
import {MultiFanCriteria} from "./useMultiFanCriteriaForm";
import {
    calculateMultiFanNoise,
    MultiFanNoiseCalculationParams,
    MultiFanNoiseFanParticipation,
    MultiFanNoiseResults
} from "./multiFanNoiseCalculations";

/**
 * Hook obliczający hałas od wszystkich wentylatorów
 */
export function useCalculateMultiFanNoise(
    multiFanCriteria: MultiFanCriteria,
    onSvgUpdate?: (svg: string) => void
): MultiFanNoiseResults | undefined {
    const [results, setResults] = useState<MultiFanNoiseResults | undefined>(undefined);

    const calculatedResults = useMemo(() => {
        // Sprawdź czy mamy wszystkie potrzebne dane
        if (!multiFanCriteria.situation || multiFanCriteria.error) {
            return undefined;
        }

        const situation = multiFanCriteria.situation;
        const coolers = situation.getCoolers();
        const observer = situation.getObserver();

        // Zbierz wszystkie wentylatory ze wszystkich chłodni
        const fanParticipations: MultiFanNoiseFanParticipation[] = [];

        for (const cooler of coolers) {
            const fans = cooler.getFans();
            for (const fan of fans) {
                fanParticipations.push({
                    pwlDba: fan.pwlDba,
                    fanNumber: fan.fanNumber,
                    absoluteFanCenterCoordinates: fan.centerCoordinates
                });
            }
        }

        // Jeśli nie ma wentylatorów, nie obliczaj
        if (fanParticipations.length === 0) {
            return undefined;
        }

        // Przygotuj parametry do obliczeń
        const calculationParams: MultiFanNoiseCalculationParams = {
            fanParticipations,
            observerCoordinates: observer.position,
            coolerWidthMeters: situation.getCoolerWidthMeters(),
            coolerHeightMeters: situation.getCoolerHeightMeters(),
            fanPwlDba: situation.getFanPwlDba(),
            additionalUserGivenFanPwlDba: situation.additionalUserGivenFanPwlDba
        };

        // Wykonaj obliczenia
        return calculateMultiFanNoise(calculationParams);
    }, [multiFanCriteria.situation, multiFanCriteria.error]);

    // Gdy mamy wyniki obliczeń, zaktualizuj Situation i przerysuj SVG
    useEffect(() => {
        if (calculatedResults && multiFanCriteria.situation) {
            // Zaktualizuj wartości SPL w Situation
            multiFanCriteria.situation.updateCalculationResults(calculatedResults);

            // Wygeneruj nowy SVG z zaktualizowanymi wartościami
            const updatedSvg = multiFanCriteria.situation.renderToSVG();

            // Powiadom o aktualizacji SVG
            if (onSvgUpdate) {
                onSvgUpdate(updatedSvg);
            }

            // Zapisz wyniki
            setResults(calculatedResults);
        }
    }, [calculatedResults, multiFanCriteria.situation, onSvgUpdate]);

    return results;
}
